/**
 * Home page — the "menu" the user lands on after signing in.
 *
 * v1 has exactly one destination: the recent analyses list. This page
 * exists rather than redirecting straight to `/analyses` because the
 * user explicitly asked for "a menu with one option" — leaving room
 * for the second, third, and Nth options to land here as we grow the
 * dashboard surface. When we add more pages (patches, trends, admin,
 * etc.), they slot in as additional cards in the same grid.
 *
 * # Layout
 *
 * - A small header with the page title + a logout control. The
 *   logout control lives in the shared `<TopNav>` component as a
 *   `<form method="post" action="/auth/google/logout">` — a plain
 *   `<a>` here would issue a GET, and the backend route is
 *   registered as POST-only, so a GET 405s and the session cookie
 *   never clears (the pre-2026 shape of this comment described the
 *   old broken-anchor behavior). The form POST matches the
 *   backend's method and auto-follows the `Redirect::to("/login")`
 *   response — full-page navigation, not SPA.
 *
 * - A greeting showing the signed-in user's email + display name,
 *   pulled from `useSession()`. Serves both as a "you are logged in
 *   as X" confirmation and as a visible marker that the session
 *   cookie round-tripped successfully.
 *
 * - A grid of menu cards. Each card is a React Router `<Link>`
 *   (SPA navigation, no page reload) wrapped around a tile with an
 *   icon, title, and one-line description. v1 has one card:
 *   "Recent Analyses" → `/analyses`.
 *
 * # Why no shared PageShell yet
 *
 * dk2 has a `PageShell` layout component with a sidebar + outlet. We
 * deliberately don't — the triage dashboard has 2 pages (Login +
 * Home + Analyses = really 3, but Login is unauthenticated), so a
 * sidebar is more chrome than content. Home renders its own header
 * and Analyses renders its own header; each is ~10 lines of JSX.
 * When we grow past ~5 authenticated pages, wrap them in a shared
 * layout component then.
 */

import { Link } from "react-router";

import { TopNav } from "@/components/TopNav";
import { useSession } from "@/lib/auth";

/**
 * Inline SVG icons used in the menu cards. Kept inline rather than
 * imported from a library (`lucide-react` etc.) because pulling in
 * an icon pack for eight glyphs would add ~50 KB to the bundle for
 * a single-purpose use. Every icon below is hand-transcribed from
 * Heroicons (public domain / MIT).
 */
// 2026-05-01: MagnifierIcon removed — the "Recent Analyses" menu
// card that used it was deleted per operator directive ("redundant
// with /bundles + /trends"). The /analyses/:uuid detail route still
// exists but isn't reachable from Home; Slack link targets + row-
// click drill-through from Bundles/Duplicates land there instead.

/** Bar chart icon used on the Activity card. Heroicons `chart-bar` simplified. */
function BarChartIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 20h18M6 20V12m6 8V8m6 12v-6"
      />
    </svg>
  );
}

/** Bell icon used on the Alerts card (TRIAG-343). Heroicons `bell` simplified. */
function BellIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
      <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    </svg>
  );
}

/** Chat-bubble icon used on the Feedback card (TRIAG-281). */
function ChatBubbleIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M4.5 5.25h15a1.5 1.5 0 0 1 1.5 1.5v9a1.5 1.5 0 0 1-1.5 1.5H12l-4.5 3.75V17.25h-3a1.5 1.5 0 0 1-1.5-1.5v-9a1.5 1.5 0 0 1 1.5-1.5Z"
      />
      <path strokeLinecap="round" d="M8 9.75h8M8 13h5" />
    </svg>
  );
}

/** People icon used on the Users card (TRIAG-427). Heroicons `users` outline. */
function UsersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
      />
    </svg>
  );
}

/** Chip icon used on the Hardware card (TRIAG-429). Heroicons `cpu-chip` outline. */
function HardwareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 0 0 2.25-2.25V6.75a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 6.75v10.5a2.25 2.25 0 0 0 2.25 2.25Zm.75-12h9v9h-9v-9Z"
      />
    </svg>
  );
}

/** Inbox icon used on the Bundles card. Heroicons `inbox` outline. */
function InboxIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12h5l1.5 3h5l1.5-3h5M4.5 4.5h15a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5h-15A1.5 1.5 0 0 1 3 18V6a1.5 1.5 0 0 1 1.5-1.5Z"
      />
    </svg>
  );
}

/** Layers / stack icon used on the Top Duplicates (Trends) card. Heroicons `squares-2x2` simplified. */
function LayersIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3.75 6 12 2.25 20.25 6 12 9.75 3.75 6Zm0 6L12 15.75 20.25 12M3.75 18 12 21.75 20.25 18"
      />
    </svg>
  );
}

/** Gear icon used on the Admin card. Heroicons `cog-6-tooth` simplified. */
function GearIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      aria-hidden="true"
      focusable="false"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M10.5 3.75h3l.4 2.1a7.5 7.5 0 0 1 2 1.15l2.05-.75 1.5 2.6-1.65 1.4a7.5 7.5 0 0 1 0 2.3l1.65 1.4-1.5 2.6-2.05-.75a7.5 7.5 0 0 1-2 1.15l-.4 2.1h-3l-.4-2.1a7.5 7.5 0 0 1-2-1.15l-2.05.75-1.5-2.6 1.65-1.4a7.5 7.5 0 0 1 0-2.3l-1.65-1.4 1.5-2.6 2.05.75a7.5 7.5 0 0 1 2-1.15l.4-2.1Z"
      />
      <circle cx="12" cy="12" r="2.75" />
    </svg>
  );
}

export function HomePage() {
  // `useSession()` is guaranteed to have data here — AuthGuard (in
  // App.tsx) only renders this page when the session query has
  // resolved successfully. Still, we defend against `data` being
  // `undefined` in case the cached session expires mid-session and a
  // refetch returns null; show a generic greeting rather than crash.
  const { data: session } = useSession();
  const displayName = session?.display_name ?? "there";

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950">
      {/*
        Shared top nav — per CONTRIBUTING §"Best practices at a
        glance" UI/UX rule 6, every top-level page renders TopNav
        as the first child of its outer <header>. Home no longer
        needs a bespoke brand/email/sign-out header — TopNav
        subsumes all three, and the card grid below still serves
        as the "menu" landing for operators who prefer to pick a
        destination from a larger target than a nav link.
      */}
      <header>
        <TopNav />
      </header>

      {/* Main content */}
      <main className="mx-auto max-w-5xl px-6 py-10">
        {/* Greeting */}
        <div className="mb-8">
          <h2 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-gray-50">
            Welcome, {displayName}.
          </h2>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Pick an option below to get started.
          </p>
        </div>

        {/*
          Menu grid. Responsive: 1 column on phones, 2 on tablets, 3
          on laptops.

          Cards are ordered by "what an operator usually wants next":
          Bundles (live firehose), Trends (recurring issues),
          Activity (system health), Admin (config). Admin is last
          because it's rarely the first click.

          2026-05-01: the "Recent Analyses" card was removed per
          operator directive — it was redundant with /bundles +
          /trends and a source of confusion. The /analyses/:uuid
          detail route stays (Slack links + row-click drill-through
          target it) but there's no longer a list-page entry point.
        */}
        <nav
          aria-label="Main menu"
          className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
        >
          <Link
            to="/bundles"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-purple-50 text-purple-700 transition-colors group-hover:bg-purple-100 dark:bg-purple-950 dark:text-purple-300 dark:group-hover:bg-purple-900">
              <InboxIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Bundles
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Every intake bundle — deduped, skipped, in-flight, completed.
              Filterable by platform, version, priority, and more.
            </p>
          </Link>

          {/*
            2026-05-01: "Recent Analyses" card removed. Per operator
            directive, the list page it linked to was redundant with
            /bundles + /trends and a source of confusion for users.
            Detail-page drill-through via /bundles row clicks,
            /duplicates navigation, and Slack link targets still
            land at /analyses/:uuid — only the list entry point went
            away.
          */}

          {/*
            2026-05-01: card label flipped from "Top Duplicates" to
            "Trends" so the Home grid matches the TopNav label
            verbatim (see TopNav.tsx::SECTIONS). Operator directive:
            "the names should match up so it's not confusing."
            Destination URL dropped the `?window=30d` query string
            because the `/trends` page's default window is now
            24h — the bare `/trends` URL lands on the primary-
            surface default. Operators wanting 30d click the
            segmented toggle on-page.
          */}
          <Link
            to="/trends"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-fuchsia-50 text-fuchsia-700 transition-colors group-hover:bg-fuchsia-100 dark:bg-fuchsia-950 dark:text-fuchsia-300 dark:group-hover:bg-fuchsia-900">
              <LayersIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Trends
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Most common recurring error signatures — defaults to the last 24h.
              Drill down into any family to see its matches.
            </p>
          </Link>

          <Link
            to="/activity"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-green-50 text-green-700 transition-colors group-hover:bg-green-100 dark:bg-green-950 dark:text-green-300 dark:group-hover:bg-green-900">
              <BarChartIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Activity
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              System activity over time — issues analyzed and issues received,
              bucketed by hour or day.
            </p>
          </Link>

          {/* TRIAG-427 — Users card; label + order match TopNav.tsx::SECTIONS. */}
          <Link
            to="/users"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-teal-50 text-teal-700 transition-colors group-hover:bg-teal-100 dark:bg-teal-950 dark:text-teal-300 dark:group-hover:bg-teal-900">
              <UsersIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Users
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Look up an app user — their reports and Oops alerts over time, the
              errors they keep hitting, their devices.
            </p>
          </Link>

          {/* TRIAG-429 — Hardware card; label + order match TopNav.tsx::SECTIONS. */}
          <Link
            to="/hardware"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-orange-50 text-orange-700 transition-colors group-hover:bg-orange-100 dark:bg-orange-950 dark:text-orange-300 dark:group-hover:bg-orange-900">
              <HardwareIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Hardware
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Battery, heat, processor and memory on the phones that sent
              reports — by device, OS and app version.
            </p>
          </Link>

          {/* TRIAG-343 — Alerts card; label + order match
              TopNav.tsx::SECTIONS. */}
          <Link
            to="/alerts"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-amber-50 text-amber-700 transition-colors group-hover:bg-amber-100 dark:bg-amber-950 dark:text-amber-300 dark:group-hover:bg-amber-900">
              <BellIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Alerts
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Trend alerts — error storms, brand-new problems, slow burns, and
              same-issue user write-ins, with delivery status.
            </p>
          </Link>

          {/* TRIAG-281 — Feedback card; label + order match
              TopNav.tsx::SECTIONS ("the names should match up"). */}
          <Link
            to="/feedback"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-sky-50 text-sky-700 transition-colors group-hover:bg-sky-100 dark:bg-sky-950 dark:text-sky-300 dark:group-hover:bg-sky-900">
              <ChatBubbleIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Feedback
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              What users report in the feedback channel — deduplicated into
              underlying issues, newest activity first.
            </p>
          </Link>

          <Link
            to="/admin"
            className="group flex flex-col rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-all hover:border-gray-300 hover:shadow-md focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700"
          >
            <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-md bg-slate-100 text-slate-700 transition-colors group-hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:group-hover:bg-slate-700">
              <GearIcon className="h-6 w-6" />
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-gray-50">
              Admin
            </h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              People and access — priority users, admins, subscriptions.
              Admin access required; the backend enforces the gate.
            </p>
          </Link>
        </nav>
      </main>

      {/* Footer — tiny build/env breadcrumb for operator sanity */}
      <footer className="mx-auto max-w-5xl px-6 pb-10 pt-4">
        <p className="text-xs text-gray-400 dark:text-gray-600">
          {session?.environment ?? "…"} · authenticated via cantina.ai Google
          Workspace
        </p>
      </footer>
    </div>
  );
}
